/**
 * XION Medical product data — Camera heads
 * Content sourced from xion-medical.com (split from completeProductsData.js).
 */

export const cameraHeads = {
  id: 'camera-heads',
  name: 'Camera Heads',
  slug: 'camera-heads',
  category: 'Camera Heads',
  description: 'Thanks to the universal Spectar Connector, a growing number of freely interchangeable application parts are available for the Spectar camera platform, whereby the 3D and 4K/UHD camera heads are supported exclusively by the P/PL processors. 2D camera heads are available with Full HD or 4K/UHD sensors and integrated lenses with fixed focal length or zoom. The compact, ergonomic Soft-Touch design and low weight make the Spectar camera heads very easy to handle. Both of the ergonomically arranged function keys are freely programmable.',
  products: [
    {
      id: 'spectar-camera-head-4k-uhd',
      name: 'Spectar Camera Head 4K/UHD',
      slug: 'spectar-camera-head-4k-uhd',
      tagline: 'Extremely detailed, crisp, sharp, bright, low-noise images',
      description: 'The high resolution 4K sensor of the Spectar Camera Head provides extremely detailed, crisp, sharp, bright, low-noise images in conjunction with the MATRIX P Spectar or the EndoSTROBE PL Spectar camera processors. The 4 times higher resolution compared to HD, plus the extended colour space, make it possible to detect very fine vessels and tissue structures much more easily – even when enlarged in a (digitally zoomed) display. This provides greater precision and safety for the surgeon. In addition, visibility and ease of use are greatly enhanced.',
      features: [
        '4K/UHD sensor resolution',
        'Extremely detailed, crisp image quality',
        '4x higher resolution vs HD',
        'Extended colour space',
        'Lightweight and ergonomic design',
        'Individually programmable buttons',
        'Available with zoom (16-32mm) or fixed focal length (22mm)',
        'Compatible with MATRIX P/PL and EndoSTROBE PL processors'
      ],
      specifications: {
        resolution: '4K/UHD',
        lens: ['Zoom 16-32mm', 'Fixed 22mm'],
        connector: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/spectar-kamerakopf-4k-uhd-web.png']
    },
    {
      id: 'spectar-camera-head-hd-zoom',
      name: 'Spectar Camera Head HD Zoom',
      slug: 'spectar-camera-head-hd-zoom',
      tagline: 'Detailed, low-noise image reproduction with optical zoom',
      description: 'The highly sensitive Full HD sensor of the Spectar Camera Head provides detailed, low-noise image reproduction. An optical Parfocal Zoom (f = 16 through 32 mm) empowers the Spectar Camera Head HD Zoom to individually adapt the image size without compromising quality and without the need for refocusing.',
      features: [
        'Full HD sensor',
        'Optical Parfocal Zoom (16-32mm)',
        'No refocusing needed when zooming',
        'Lightweight and ergonomic design',
        'Individually programmable buttons',
        'Detailed, low-noise images'
      ],
      specifications: {
        resolution: 'FullHD',
        lens: 'Parfocal Zoom 16-32mm',
        connector: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/spectar-kamerakopf-hd-zoom-web.png']
    },
    {
      id: 'spectar-camera-head-hd-microphone',
      name: 'Spectar Camera Head HD with Microphone',
      slug: 'spectar-camera-head-hd-microphone',
      tagline: 'HD camera head with integrated microphone for laryngostroboscopy',
      description: 'The HD camera head with integrated microphone, specially optimised for laryngostroboscopy with rigid endoscopes, allows synchronisation to the fundamental voice frequency and sound recording without using an external microphone.',
      features: [
        'Full HD sensor',
        'Integrated microphone',
        'Optimized for laryngostroboscopy',
        'Synchronization to fundamental voice frequency',
        'Sound recording without external mic',
        'Lightweight ergonomic design'
      ],
      specifications: {
        resolution: 'FullHD',
        specialFeatures: 'Integrated Microphone',
        connector: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/spectar-kamerakopf-hd-mit-mikrofon-web.png']
    },
    {
      id: 'spectar-camera-head-3d-hd',
      name: 'Spectar Camera Head 3D HD',
      slug: 'spectar-camera-head-3d-hd',
      tagline: 'Brilliant 3D images with XION 3D Endoscope attachments',
      description: 'In conjunction with XION 3D Endoscope attachments, the extremely compact, lightweight Spectar 3D Camera Head provides brilliant 3D images. The modular overall concept and the economic endoscope attachments make it possible to expand an existing Spectar Camera System to a state-of-the-art 3D Endoscopy System at just moderate cost. The XION sterile adapter between the 3D camera head and the endoscope attachment ensures that the endoscope is held securely and that sterile camera drapes can be easily attached to the adapter, thus enabling the quick change of endoscopes with different viewing directions without having to loosen the drape.',
      features: [
        '3D HD imaging',
        'Extremely compact and lightweight',
        'Compatible with XION 3D Endoscope attachments',
        'Economic upgrade to 3D endoscopy',
        'Sterile adapter for secure endoscope holding',
        'Quick endoscope changes without loosening drape'
      ],
      specifications: {
        resolution: 'FullHD 3D',
        features: ['3D'],
        connector: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/spectar-kamerakopf-3d-hd-web.png']
    },
    {
      id: 'spectar-camera-head-c-mount',
      name: 'Spectar Camera Head C-Mount',
      slug: 'spectar-camera-head-c-mount',
      tagline: 'C-Mount camera head for surgical microscopes',
      description: 'The compact Spectar C-Mount Camera Head can be easily mounted on any system with a C-Mount interface, such as appropriately equipped operating-room microscopes. The adjustable threaded ring allows the image plane to be precisely matched to the visual focus position in the eyepiece, ensuring highest quality for video recordings and for co-observation.',
      features: [
        'Mounts on any C-Mount interface (e.g. OR microscopes)',
        'Adjustable threaded ring matches the image plane to the eyepiece focus',
        'Highest-quality video recording and co-observation',
        'Available with Full HD or 4K/UHD sensor',
        'Compact, lightweight Soft-Touch design'
      ],
      specifications: {
        interface: 'C-Mount',
        resolution: 'Full HD / 4K UHD',
        application: 'OR microscopes · co-observation',
        connector: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/spectar-kamerakopf-c-mount-web.png']
    },
    {
      id: 'spectar-microscope-camera-3d',
      name: 'Spectar Microscope Camera 3D 4K/UHD',
      slug: 'spectar-microscope-camera-3d',
      tagline: '3D co-observation and recording for surgical microscopes',
      description: "XION's Spectar 3D Microscope Camera facilitates 3D co-observation of the microscope image on a 3D monitor as well as 3D video recording. A useful extension of the MATRIX Spectar System, it can be combined with most Carl Zeiss and LEICA surgical microscopes. Thanks to rapid switching between endoscopic and microscopic image acquisition on a common image processor, it enables the combined use of 3D microscopy and 3D or 2D endoscopy in one session.",
      features: [
        '3D co-observation on a 3D monitor and 3D video recording',
        'Combines with most Carl Zeiss and LEICA surgical microscopes',
        'A useful extension of the MATRIX Spectar System',
        'Rapid switching between microscopic and endoscopic acquisition',
        'Combined 3D microscopy + 3D/2D endoscopy in one session',
        '4K/UHD resolution'
      ],
      specifications: {
        type: '3D microscope camera',
        resolution: '4K/UHD · 3D',
        compatibility: 'Carl Zeiss & LEICA microscopes · MATRIX Spectar',
        connector: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/spectar-mikroskopkamera-3d-4k-uhd-web.png']
    }
  ]
};
